import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save, X, Link as LinkIcon, Upload, Tag,
  FileText, Hash, Calendar, User, Book,
  Bold, Italic, List, ListOrdered, Link,
  Package, Layers, Filter, Folder,
  AlertCircle, CheckCircle, HelpCircle,
  FileCode, FileText as FileTextIcon, Settings,
  Clock, AlertTriangle, Eye, EyeOff, Maximize2, Type, Image,
  Heading1, Heading2, Heading3, Code, Quote, Table, Columns,
  Paperclip, Trash2
} from 'lucide-react';
import {
  Input, Button, Card, Form, Select, Upload as AntUpload,
  Switch, message, Spin, Modal, Typography, Row, Col, Divider, Alert,
  Badge, Tooltip, Tag as AntTag, Tabs, Space, Radio, List as AntList
} from 'antd';
import api from '../../utils/api';
import './KnowledgeBase.css';

const { TextArea } = Input;
const { Option } = Select;
const { Title, Text } = Typography;
const { TabPane } = Tabs;

// Article Type options (hardcoded)
const ARTICLE_TYPES = [
  { id: 'troubleshooting', name: 'Troubleshooting Guide', icon: <AlertCircle size={16} /> },
  { id: 'howto', name: 'How-To Guide', icon: <HelpCircle size={16} /> },
  { id: 'sop', name: 'Standard Operating Procedure', icon: <FileTextIcon size={16} /> },
  { id: 'admin', name: 'Administrator Guide', icon: <Settings size={16} /> },
  { id: 'reference', name: 'Reference Documentation', icon: <Book size={16} /> },
  { id: 'faq', name: 'FAQ', icon: <HelpCircle size={16} /> },
  { id: 'tutorial', name: 'Tutorial', icon: <FileCode size={16} /> },
  { id: 'knowledge', name: 'Knowledge Base Article', icon: <FileText size={16} /> },
  { id: 'other', name: 'Other', icon: <FileText size={16} /> }
];

const CreateArticlePage = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [fetchingData, setFetchingData] = useState(true);
  const [fetchingTicketDetails, setFetchingTicketDetails] = useState(false);
  const [content, setContent] = useState('');
  const [showTicketLink, setShowTicketLink] = useState(false);
  const [ticketOptions, setTicketOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [productOptions, setProductOptions] = useState([]);
  const [priorityOptions, setPriorityOptions] = useState([]);
  const [productCategoryOptions, setProductCategoryOptions] = useState([]);
  const [linkedTicket, setLinkedTicket] = useState(null);
  const [ticketDetails, setTicketDetails] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [editorMode, setEditorMode] = useState('split'); // 'edit', 'preview', 'split'
  const [wordCount, setWordCount] = useState(0);
  const [charCount, setCharCount] = useState(0);
  const [fileList, setFileList] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await api.get('/api/users/profile');
        if (response.data && response.data.data) {
          setCurrentUser(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };

    fetchCurrentUser();
    const fetchDropdownData = async () => {
      setFetchingData(true);
      try {
        console.log('Starting to fetch dropdown data...');
        
        const [
          prioritiesRes,
          categoriesRes,
          productsRes,
          productCategoriesRes,
          ticketsRes
        ] = await Promise.all([
          api.get('/api/priorities'),
          api.get('/api/ticket-categories'),
          api.get('/api/products'),
          api.get('/api/product-categories'),
          api.get('/api/tickets', {
            params: { 
              limit: 100, 
              sort: 'created_at:desc'
            }
          })
        ]);

        let prioritiesData = [];
        if (Array.isArray(prioritiesRes.data)) {
          prioritiesData = prioritiesRes.data;
        } else if (prioritiesRes.data && Array.isArray(prioritiesRes.data.data)) {
          prioritiesData = prioritiesRes.data.data;
        }
        setPriorityOptions(prioritiesData);

        let categoriesData = [];
        if (Array.isArray(categoriesRes.data)) {
          categoriesData = categoriesRes.data;
        } else if (categoriesRes.data && Array.isArray(categoriesRes.data.data)) {
          categoriesData = categoriesRes.data.data;
        }
        setCategoryOptions(categoriesData);

        let productsData = [];
        if (Array.isArray(productsRes.data)) {
          productsData = productsRes.data;
        } else if (productsRes.data && Array.isArray(productsRes.data.data)) {
          productsData = productsRes.data.data;
        }
        setProductOptions(productsData);

        let productCategoriesData = [];
        if (Array.isArray(productCategoriesRes.data)) {
          productCategoriesData = productCategoriesRes.data;
        } else if (productCategoriesRes.data && Array.isArray(productCategoriesRes.data.data)) {
          productCategoriesData = productCategoriesRes.data.data;
        }
        setProductCategoryOptions(productCategoriesData);

        let ticketsData = [];
        if (Array.isArray(ticketsRes.data)) {
          ticketsData = ticketsRes.data;
        } else if (ticketsRes.data && Array.isArray(ticketsRes.data.data)) {
          ticketsData = ticketsRes.data.data;
        } else if (ticketsRes.data && ticketsRes.data.data && Array.isArray(ticketsRes.data.data.items)) {
          ticketsData = ticketsRes.data.data.items;
        }

        const formattedTickets = ticketsData.map(ticket => {
          const ticketData = ticket.data || ticket;
          
          let formattedDate = 'No date';
          const createdAt = ticketData.createdAt || ticketData.created_at;
          
          if (createdAt) {
            try {
              let dateStr = createdAt;
              
              if (typeof dateStr === 'string') {
                if (dateStr.includes(' ') && !dateStr.includes('T')) {
                  dateStr = dateStr.replace(' ', 'T');
                }
                if (!dateStr.includes('Z') && !dateStr.includes('+') && dateStr.includes('T')) {
                  dateStr += 'Z';
                }
              }
              
              const date = new Date(dateStr);
              
              if (!isNaN(date.getTime())) {
                formattedDate = date.toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                });
              } else {
                console.warn('Invalid date after parsing:', dateStr);
              }
            } catch (error) {
              console.warn('Date parsing error:', createdAt, error);
            }
          }
          
          return {
            id: ticketData.id,
            uuid: ticketData.ticketUuid || ticketData.ticket_uuid || ticketData.uuid,
            title: ticketData.title,
            formattedDate: formattedDate,
            createdAt: createdAt,
            ticketCategoryId: ticketData.ticketCategoryId || ticketData.ticket_category_id,
            priorityId: ticketData.priorityId || ticketData.priority_id,
            productId: ticketData.productId || ticketData.product_id,
            productCategoryId: ticketData.productCategoryId || ticketData.product_category_id,
            description: ticketData.description
          };
        });
        
        setTicketOptions(formattedTickets);
        console.log('Tickets fetched:', formattedTickets.length);

      } catch (error) {
        console.error('Error fetching dropdown data:', error);
        message.error('Failed to load form data');
        
        setTicketOptions([]);
        setCategoryOptions([]);
        setProductOptions([]);
        setPriorityOptions([]);
        setProductCategoryOptions([]);
      } finally {
        setFetchingData(false);
      }
    };

    fetchDropdownData();
  }, []);

  useEffect(() => {
    // Calculate word and character counts
    const words = content.trim() ? content.trim().split(/\s+/).length : 0;
    const chars = content.length;
    setWordCount(words);
    setCharCount(chars);
  }, [content]);

  const handleFileUpload = async (info) => {
    if (info.file.status === 'uploading') {
      setUploading(true);
      return;
    }

    if (info.file.status === 'done') {
      setUploading(false);
      const { file } = info;
      
      if (file.response && file.response.success) {
        const uploadedFile = {
          uid: file.uid,
          name: file.name,
          status: 'done',
          url: file.response.url || file.response.path,
          size: file.size,
          type: file.type
        };
        
        setUploadedFiles(prev => [...prev, uploadedFile]);
        message.success(`${file.name} uploaded successfully`);
      } else {
        message.error(`${file.name} upload failed.`);
      }
    } else if (info.file.status === 'error') {
      setUploading(false);
      message.error(`${info.file.name} upload failed.`);
    }
  };

  const handleRemoveFile = (file) => {
    setUploadedFiles(prev => prev.filter(f => f.uid !== file.uid));
    return true;
  };

  const beforeUpload = (file) => {
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('File must be smaller than 10MB!');
      return false;
    }
    
    const allowedTypes = [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/svg+xml',
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ];
    
    if (!allowedTypes.includes(file.type)) {
      message.error('File type not supported!');
      return false;
    }
    
    return true;
  };

const handleSubmit = async (values) => {
  setLoading(true);
  try {
    // Get ALL form values including category/product fields
    const formValues = form.getFieldsValue();
    
    console.log('🔍 Form values:', formValues);
    console.log('📝 Values from onFinish:', values);
    console.log('📎 Files to upload:', fileList);
    console.log('📎 Origin files:', fileList.map(f => ({
      name: f.name,
      hasOriginFileObj: !!f.originFileObj
    })));
    
    const articleData = {
      title: values.title,
      content: content,
      excerpt: values.excerpt || '',
      type_name: values.type_name,
      category_name: formValues.category || null,
      product_name: formValues.product || null,
      product_category_name: formValues.productCategory || null,
      priority_id: formValues.priority || null,
      ticket_id: linkedTicket?.id || null,
      ticket_uuid: linkedTicket?.uuid || null,
      tags: values.tags || [],
      status: values.status || 'draft'
    };

    console.log('📤 Submitting article with ALL fields:', articleData);
    
    // 1. Create the article first
    const response = await api.post('/api/kb-articles', articleData);
    
    if (!response.data.success) {
      throw new Error(response.data.message || 'Failed to save article');
    }
    
    const articleId = response.data.data.id || response.data.data.articleUuid;
    console.log('✅ Article created with ID:', articleId);
    console.log('✅ Article database ID:', response.data.data.id);
    
    // 2. Upload attachments if there are any
    if (fileList.length > 0) {
      message.info(`Uploading ${fileList.length} attachment(s)...`);
      
      // Filter only files that have originFileObj (real files, not just metadata)
      const filesToUpload = fileList.filter(file => file.originFileObj);
      console.log('📤 Files with originFileObj:', filesToUpload.length);
      
      if (filesToUpload.length === 0) {
        console.warn('No files have originFileObj! Files might be just metadata.');
        message.warning('Files selected but no actual file data found.');
      }
      
      const uploadPromises = filesToUpload.map(async (file) => {
        const formData = new FormData();
        formData.append('attachment', file.originFileObj);
        
        console.log(`📤 Uploading ${file.name} to article ${articleId}...`);
        
        try {
          const uploadResponse = await api.post(
            `/api/kb-articles/${articleId}/attachments`,
            formData,
            {
              headers: {
                'Content-Type': 'multipart/form-data',
              }
            }
          );
          
          console.log(`✅ Upload response for ${file.name}:`, uploadResponse.data);
          return { success: true, file: file.name };
          
        } catch (error) {
          console.error(`❌ Failed to upload ${file.name}:`, error);
          if (error.response) {
            console.error('Error details:', error.response.data);
          }
          return { 
            success: false, 
            file: file.name, 
            error: error.response?.data?.message || error.message 
          };
        }
      });
      
      if (uploadPromises.length > 0) {
        const uploadResults = await Promise.all(uploadPromises);
        const successfulUploads = uploadResults.filter(r => r.success);
        const failedUploads = uploadResults.filter(r => !r.success);
        
        console.log('📊 Upload results:', {
          total: fileList.length,
          success: successfulUploads.length,
          failed: failedUploads.length
        });
        
        if (failedUploads.length > 0) {
          console.warn('Some files failed to upload:', failedUploads);
          message.warning(
            `${successfulUploads.length}/${fileList.length} files uploaded successfully. ` +
            `${failedUploads.length} failed: ${failedUploads.map(f => f.file).join(', ')}`
          );
        } else {
          message.success(`All ${fileList.length} file(s) uploaded successfully!`);
        }
      } else {
        console.warn('No files to upload (all files missing originFileObj)');
        message.warning('No actual files were uploaded - please reselect the files.');
      }
    }
    
    message.success('Article saved successfully!');
    navigate('/knowledge-base');
    
  } catch (error) {
    console.error('❌ Save error:', error);
    
    if (error.response) {
      console.error('Error response:', error.response.data);
      const errorMsg = error.response.data?.message || error.response.data?.error || 'Failed to save article';
      message.error(`Error: ${errorMsg}`);
      
      if (error.response.data?.errors) {
        console.error('Validation errors:', error.response.data.errors);
        const errors = {};
        error.response.data.errors.forEach(err => {
          if (err.field) {
            errors[err.field] = {
              value: form.getFieldValue(err.field),
              errors: [new Error(err.message)]
            };
          }
        });
        form.setFields(errors);
      }
    } else {
      message.error(error.message || 'Failed to save article');
    }
  } finally {
    setLoading(false);
  }
};

  const handleTicketSelect = async (ticketId) => {
    if (!ticketId) {
      clearLinkedTicket();
      return;
    }
    
    const selectedTicket = ticketOptions.find(t => t.id === ticketId);
    if (!selectedTicket) {
      message.error('Selected ticket not found');
      return;
    }
    
    setLinkedTicket(selectedTicket);
    setFetchingTicketDetails(true);
    
    try {
      console.log('Fetching ticket details for ticket ID:', ticketId);
      const response = await api.get(`/api/tickets/${ticketId}`);
      
      let ticketDetail = response.data.data || response.data;
      console.log('Full ticket details response:', ticketDetail);
      
      setTicketDetails(ticketDetail);
      
      const updates = {
        title: `KB: ${ticketDetail.title || selectedTicket.title}`,
      };
      
      // CRITICAL: Make sure to set ALL form fields
      if (ticketDetail.priorityId || ticketDetail.priority_id) {
        updates.priority = ticketDetail.priorityId || ticketDetail.priority_id;
      }
      
      if (ticketDetail.ticketCategoryName) {
        updates.category = ticketDetail.ticketCategoryName;
      } else if (ticketDetail.ticket_category_name) {
        updates.category = ticketDetail.ticket_category_name;
      } else {
        updates.category = null; // Explicitly set to null if not available
      }
      
      if (ticketDetail.productName) {
        updates.product = ticketDetail.productName;
      } else if (ticketDetail.product_name) {
        updates.product = ticketDetail.product_name;
      } else {
        updates.product = null;
      }
      
      if (ticketDetail.productCategoryName) {
        updates.productCategory = ticketDetail.productCategoryName;
      } else if (ticketDetail.product_category_name) {
        updates.productCategory = ticketDetail.product_category_name;
      } else {
        updates.productCategory = null;
      }
      
      console.log('📝 Setting form fields with ticket data:', updates);
      form.setFieldsValue(updates);
      
      // Also update the linkedTicket object with extracted values
      setLinkedTicket({
        ...selectedTicket,
        categoryName: updates.category,
        productName: updates.product,
        productCategoryName: updates.productCategory,
        priorityId: updates.priority
      });
      
      const description = ticketDetail.description || selectedTicket.description;
      if (description) {
        setContent(`## Problem Description\n\n${description}\n\n## Solution\n\n[Provide step-by-step solution here...]\n\n## Additional Notes\n\n[Any additional information or troubleshooting tips...]`);
      } else {
        setContent(`## Problem Description\n\n[Describe the problem from ticket ${selectedTicket.uuid}...]\n\n## Solution\n\n[Provide step-by-step solution here...]\n\n## Additional Notes\n\n[Any additional information...]`);
      }
      
      message.success(`Linked to ticket ${selectedTicket.uuid}. Fields auto-populated.`);
      
    } catch (error) {
      console.error('Error fetching ticket details:', error);
      message.error('Failed to load ticket details, using basic info');
      
      const updates = {
        title: `KB: ${selectedTicket.title}`,
      };
      
      if (selectedTicket.priorityId || selectedTicket.priority_id) {
        updates.priority = selectedTicket.priorityId || selectedTicket.priority_id;
      }
      
      // Make sure to set category/product fields even if null
      updates.category = null;
      updates.product = null;
      updates.productCategory = null;
      
      form.setFieldsValue(updates);
      
      // Update linkedTicket
      setLinkedTicket({
        ...selectedTicket,
        priorityId: updates.priority
      });
      
      if (selectedTicket.description) {
        setContent(`## Problem Description\n\n${selectedTicket.description}\n\n## Solution\n\n[Provide step-by-step solution here...]\n\n## Additional Notes\n\n[Any additional information...]`);
      }
      
    } finally {
      setFetchingTicketDetails(false);
    }
  };

  const formatText = (format) => {
    const textarea = document.getElementById('content-textarea');
    if (!textarea) return;
    
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = content.substring(start, end);
    let newText = content;
    let cursorAdjust = 0;
    
    switch (format) {
      case 'bold':
        newText = content.substring(0, start) + `**${selectedText}**` + content.substring(end);
        cursorAdjust = selectedText.length ? selectedText.length + 4 : 4;
        break;
      case 'italic':
        newText = content.substring(0, start) + `*${selectedText}*` + content.substring(end);
        cursorAdjust = selectedText.length ? selectedText.length + 2 : 2;
        break;
      case 'h1':
        newText = content.substring(0, start) + `# ${selectedText}\n` + content.substring(end);
        cursorAdjust = selectedText.length ? selectedText.length + 3 : 3;
        break;
      case 'h2':
        newText = content.substring(0, start) + `## ${selectedText}\n` + content.substring(end);
        cursorAdjust = selectedText.length ? selectedText.length + 4 : 4;
        break;
      case 'h3':
        newText = content.substring(0, start) + `### ${selectedText}\n` + content.substring(end);
        cursorAdjust = selectedText.length ? selectedText.length + 5 : 5;
        break;
      case 'bullet':
        newText = content.substring(0, start) + `\n* ${selectedText}` + content.substring(end);
        cursorAdjust = selectedText.length ? selectedText.length + 3 : 3;
        break;
      case 'number':
        newText = content.substring(0, start) + `\n1. ${selectedText}` + content.substring(end);
        cursorAdjust = selectedText.length ? selectedText.length + 4 : 4;
        break;
      case 'link':
        const url = prompt('Enter URL:', 'https://');
        if (url) {
          newText = content.substring(0, start) + `[${selectedText || 'link'}](${url})` + content.substring(end);
          cursorAdjust = (selectedText || 'link').length + url.length + 4;
        }
        break;
      case 'code':
        newText = content.substring(0, start) + `\`\`\`\n${selectedText}\n\`\`\`\n` + content.substring(end);
        cursorAdjust = selectedText.length + 9;
        break;
      case 'quote':
        newText = content.substring(0, start) + `> ${selectedText}\n` + content.substring(end);
        cursorAdjust = selectedText.length + 3;
        break;
      default:
        break;
    }
    
    setContent(newText);
    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + (selectedText.length ? 0 : cursorAdjust);
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 10);
  };

  const clearLinkedTicket = () => {
    setLinkedTicket(null);
    setTicketDetails(null);
    setShowTicketLink(false);
    // Clear ALL related fields
    form.setFieldsValue({
      ticketId: undefined,
      title: '',
      priority: undefined,
      category: undefined,
      product: undefined,
      productCategory: undefined
    });
    setContent('');
    message.info('Ticket link cleared');
  };

  const getTicketInfoDisplay = () => {
    if (!ticketDetails) return null;
    
    const info = [];
    
    if (ticketDetails.priorityName || ticketDetails.priority_name) {
      info.push({
        label: 'Priority',
        value: ticketDetails.priorityName || ticketDetails.priority_name,
        color: ticketDetails.priorityColor || '#1890ff'
      });
    }
    
    if (ticketDetails.ticketCategoryName || ticketDetails.ticket_category_name) {
      info.push({
        label: 'Category',
        value: ticketDetails.ticketCategoryName || ticketDetails.ticket_category_name
      });
    }
    
    if (ticketDetails.productName || ticketDetails.product_name) {
      info.push({
        label: 'Product',
        value: ticketDetails.productName || ticketDetails.product_name
      });
    }
    
    if (ticketDetails.productCategoryName || ticketDetails.product_category_name) {
      info.push({
        label: 'Product Category',
        value: ticketDetails.productCategoryName || ticketDetails.product_category_name
      });
    }
    
    if (ticketDetails.createdAt || ticketDetails.created_at) {
      const dateStr = ticketDetails.createdAt || ticketDetails.created_at;
      try {
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          const formattedDate = date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          });
          info.push({
            label: 'Created',
            value: formattedDate,
            icon: <Clock size={12} />
          });
        }
      } catch (error) {
        console.warn('Date formatting error:', error);
      }
    }
    
    return info;
  };

  const renderMarkdownPreview = () => {
    // Basic markdown parsing for preview
    let html = content
      .replace(/^# (.*$)/gim, '<h1 class="markdown-h1">$1</h1>')
      .replace(/^## (.*$)/gim, '<h2 class="markdown-h2">$1</h2>')
      .replace(/^### (.*$)/gim, '<h3 class="markdown-h3">$1</h3>')
      .replace(/^#### (.*$)/gim, '<h4 class="markdown-h4">$1</h4>')
      .replace(/^##### (.*$)/gim, '<h5 class="markdown-h5">$1</h5>')
      .replace(/^###### (.*$)/gim, '<h6 class="markdown-h6">$1</h6>')
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`(.*?)`/g, '<code class="markdown-code">$1</code>')
      .replace(/```([\s\S]*?)```/g, '<pre class="markdown-pre"><code>$1</code></pre>')
      .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" class="markdown-link">$1</a>')
      .replace(/^\> (.*$)/gim, '<blockquote class="markdown-blockquote">$1</blockquote>')
      .replace(/^\* (.*$)/gim, '<ul class="markdown-list"><li>$1</li></ul>')
      .replace(/^\- (.*$)/gim, '<ul class="markdown-list"><li>$1</li></ul>')
      .replace(/^\d+\. (.*$)/gim, '<ol class="markdown-list"><li>$1</li></ol>')
      .replace(/\n\n/g, '</p><p>')
      .replace(/\n/g, '<br/>');
    
    // Wrap in paragraph if needed
    if (!html.startsWith('<')) {
      html = '<p>' + html + '</p>';
    }
    
    return { __html: html };
  };

  if (fetchingData) {
    return (
      <div className="kb-page">
        <div className="kb-header">
          <Title level={1} className="kb-title">Create Article</Title>
        </div>
        <div className="kb-main-card">
          <div className="kb-content" style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '20px', color: '#718096' }}>
              Loading form data...
            </div>
          </div>
        </div>
      </div>
    );
  }

  const ticketInfo = getTicketInfoDisplay();

  return (
    <div className="kb-page">
      {/* Header */}
      <div className="kb-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <Title level={1} className="kb-title">Create Article</Title>
            <Text className="kb-description">
              Create a new knowledge base article. Link to an existing ticket or create a standalone article.
            </Text>
          </div>
          <Button
            type="text"
            icon={<X size={20} />}
            onClick={() => navigate('/knowledge-base')}
            className="kb-cancel-button"
            style={{ color: 'white' }}
          >
            Cancel
          </Button>
        </div>
      </div>

      <div className="kb-main-card">
        <div className="kb-content">
          {/* Linked Ticket Banner */}
          {linkedTicket && (
            <Alert
              message={
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <CheckCircle size={18} color="#52c41a" />
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '14px' }}>
                        Linked to Ticket: {linkedTicket.uuid}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                        {linkedTicket.title}
                        {linkedTicket.formattedDate !== 'No date' && (
                          <span style={{ marginLeft: '12px', color: '#888' }}>
                            <Clock size={11} style={{ marginRight: '4px', verticalAlign: 'middle' }} />
                            {linkedTicket.formattedDate}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button 
                    type="link" 
                    onClick={clearLinkedTicket}
                    style={{ color: '#ff4d4f', fontSize: '13px' }}
                    disabled={fetchingTicketDetails}
                    size="small"
                  >
                    {fetchingTicketDetails ? <Spin size="small" /> : 'Unlink Ticket'}
                  </Button>
                </div>
              }
              type="success"
              style={{ marginBottom: 24, borderRadius: '8px' }}
              showIcon={false}
            />
          )}

          {/* Ticket Information Display */}
          {ticketInfo && ticketInfo.length > 0 && (
            <Card className="kb-form-section" style={{ marginBottom: '24px' }}>
              <div className="kb-section-title">
                <LinkIcon size={18} />
                Ticket Information (Auto-filled)
              </div>
              <div className="kb-ticket-info-grid">
                {ticketInfo.map((item, index) => (
                  <div key={index} className="kb-ticket-info-item">
                    <div className="kb-ticket-info-label">{item.label}:</div>
                    <div className="kb-ticket-info-value">
                      {item.icon && <span style={{ marginRight: '6px' }}>{item.icon}</span>}
                      {item.color ? (
                        <span style={{ color: item.color, fontWeight: 500 }}>{item.value}</span>
                      ) : (
                        <span style={{ fontWeight: 500 }}>{item.value}</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            className="article-form"
            initialValues={{
              status: 'draft',
              articleType: 'knowledge',
              category: null,
              product: null,
              productCategory: null,
              priority: null
            }}
          >
            <Row gutter={[32, 32]}>
              {/* Left Column - Main Content */}
              <Col xs={24} lg={16}>
                {/* Article Content Card */}
                <Card className="kb-form-section">
                  <div className="kb-section-title">
                    <FileText size={18} />
                    Article Content
                  </div>
                  
                  <Form.Item
                    label="Title"
                    name="title"
                    rules={[{ required: true, message: 'Please enter article title' }]}
                  >
                    <Input
                      placeholder="Enter article title"
                      size="large"
                      className="kb-form-input"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Excerpt / Summary"
                    name="excerpt"
                    rules={[{ required: true, message: 'Please enter a brief summary' }]}
                  >
                    <TextArea
                      placeholder="Brief summary of the article (appears in search results and lists)"
                      rows={3}
                      maxLength={200}
                      showCount
                      className="kb-form-textarea"
                    />
                  </Form.Item>

                  <Form.Item
                    label="Content"
                    rules={[{ required: true, message: 'Please enter article content' }]}
                  >
                    <div className="kb-editor-container">
                      <div className="kb-editor-header">
                        <div className="kb-editor-mode-toggle">
                          <Radio.Group 
                            value={editorMode} 
                            onChange={(e) => setEditorMode(e.target.value)}
                            size="small"
                            buttonStyle="solid"
                          >
                            <Radio.Button value="edit">
                              <Type size={14} /> Edit
                            </Radio.Button>
                            <Radio.Button value="split">
                              <Columns size={14} /> Split View
                            </Radio.Button>
                            <Radio.Button value="preview">
                              <Eye size={14} /> Preview
                            </Radio.Button>
                          </Radio.Group>
                        </div>
                        
                        <div className="kb-editor-stats">
                          <span className="kb-stat-item">
                            <Type size={12} /> Words: {wordCount}
                          </span>
                          <span className="kb-stat-item">
                            <Hash size={12} /> Characters: {charCount}
                          </span>
                        </div>
                      </div>

                      <div className="kb-editor-toolbar">
                        <Space size={[4, 0]} wrap>
                          <Tooltip title="Heading 1">
                            <Button 
                              type="text" 
                              icon={<Heading1 size={16} />} 
                              onClick={() => formatText('h1')}
                              className="kb-format-button"
                              size="small"
                            />
                          </Tooltip>
                          <Tooltip title="Heading 2">
                            <Button 
                              type="text" 
                              icon={<Heading2 size={16} />} 
                              onClick={() => formatText('h2')}
                              className="kb-format-button"
                              size="small"
                            />
                          </Tooltip>
                          <Tooltip title="Heading 3">
                            <Button 
                              type="text" 
                              icon={<Heading3 size={16} />} 
                              onClick={() => formatText('h3')}
                              className="kb-format-button"
                              size="small"
                            />
                          </Tooltip>
                          <Divider type="vertical" />
                          <Tooltip title="Bold">
                            <Button 
                              type="text" 
                              icon={<Bold size={16} />} 
                              onClick={() => formatText('bold')}
                              className="kb-format-button"
                              size="small"
                            />
                          </Tooltip>
                          <Tooltip title="Italic">
                            <Button 
                              type="text" 
                              icon={<Italic size={16} />} 
                              onClick={() => formatText('italic')}
                              className="kb-format-button"
                              size="small"
                            />
                          </Tooltip>
                          <Divider type="vertical" />
                          <Tooltip title="Bullet List">
                            <Button 
                              type="text" 
                              icon={<List size={16} />} 
                              onClick={() => formatText('bullet')}
                              className="kb-format-button"
                              size="small"
                            />
                          </Tooltip>
                          <Tooltip title="Numbered List">
                            <Button 
                              type="text" 
                              icon={<ListOrdered size={16} />} 
                              onClick={() => formatText('number')}
                              className="kb-format-button"
                              size="small"
                            />
                          </Tooltip>
                          <Divider type="vertical" />
                          <Tooltip title="Insert Link">
                            <Button 
                              type="text" 
                              icon={<Link size={16} />} 
                              onClick={() => formatText('link')}
                              className="kb-format-button"
                              size="small"
                            />
                          </Tooltip>
                          <Tooltip title="Code Block">
                            <Button 
                              type="text" 
                              icon={<Code size={16} />} 
                              onClick={() => formatText('code')}
                              className="kb-format-button"
                              size="small"
                            />
                          </Tooltip>
                          <Tooltip title="Blockquote">
                            <Button 
                              type="text" 
                              icon={<Quote size={16} />} 
                              onClick={() => formatText('quote')}
                              className="kb-format-button"
                              size="small"
                            />
                          </Tooltip>
                        </Space>
                      </div>
                      
                      <div className={`kb-editor-content ${editorMode}`}>
                        {(editorMode === 'edit' || editorMode === 'split') && (
                          <div className={`kb-editor-textarea-container ${editorMode === 'split' ? 'split-left' : 'full'}`}>
                            <TextArea
                              id="content-textarea"
                              value={content}
                              onChange={(e) => setContent(e.target.value)}
                              placeholder={`# Write your article here...

## Problem Description
Describe the issue or problem...

## Solution Steps
1. First step...
2. Second step...
3. Third step...

## Additional Notes
Any additional information or troubleshooting tips...

Use **bold**, *italic*, \`code\`, and [links](url) for formatting.`}
                              rows={editorMode === 'split' ? 20 : 25}
                              className="kb-content-textarea"
                              autoSize={editorMode === 'split' ? { minRows: 20 } : { minRows: 25 }}
                            />
                          </div>
                        )}
                        
                        {(editorMode === 'preview' || editorMode === 'split') && (
                          <div className={`kb-editor-preview-container ${editorMode === 'split' ? 'split-right' : 'full'}`}>
                            <div className="kb-preview-header">
                              <Eye size={16} />
                              <span>Live Preview</span>
                            </div>
                            <div 
                              className="kb-preview-content markdown-body"
                              dangerouslySetInnerHTML={renderMarkdownPreview()}
                            />
                          </div>
                        )}
                      </div>
                      
                      <div className="kb-editor-footer">
                        <div className="kb-format-hint">
                          <span>Markdown supported:</span>
                          <span className="kb-hint-item">**bold**</span>
                          <span className="kb-hint-item">*italic*</span>
                          <span className="kb-hint-item"># Heading 1</span>
                          <span className="kb-hint-item">[links](url)</span>
                          <span className="kb-hint-item">```code blocks```</span>
                        </div>
                      </div>
                    </div>
                  </Form.Item>
                </Card>

                {/* Attachments Card */}
                {/* Attachments Card */}
                <Card className="kb-form-section">
                <div className="kb-section-title">
                    <Upload size={18} />
                    Attachments
                </div>
                
                <div className="kb-upload-section">
                    <AntUpload
                    name="attachment"
                    multiple={true}
                    fileList={fileList}
                    beforeUpload={(file) => {
                        // Validate first
                        const isValid = beforeUpload(file);
                        if (!isValid) return false;
                        
                        // Create file object
                        const fileObj = {
                        uid: `file-${Date.now()}`,
                        name: file.name,
                        status: 'done',
                        size: file.size,
                        type: file.type,
                        originFileObj: file
                        };
                        
                        // Add to fileList
                        setFileList(prev => [...prev, fileObj]);
                        
                        // Don't auto-upload
                        return false;
                    }}
                    onRemove={(file) => {
                        setFileList(prev => prev.filter(item => item.uid !== file.uid));
                        return true;
                    }}
                    onChange={(info) => {
                        // Sync with Ant's internal state for proper UI updates
                        const { file, fileList } = info;
                        
                        if (file.status === 'removed') {
                        setFileList(fileList);
                        }
                    }}
                    disabled={uploading || loading}
                    >
                    <div className="kb-upload-area">
                        <Upload size={24} />
                        <div className="kb-upload-text">Click or drag files to upload</div>
                        <div className="kb-upload-hint">
                        Files will be uploaded after article is created
                        <br />
                        Support images, PDF, Word documents (Max: 10MB per file)
                        </div>
                    </div>
                    </AntUpload>

                    {fileList.length > 0 && (
                    <div className="kb-uploaded-files">
                        <Divider style={{ margin: '16px 0' }} />
                        <div className="kb-section-subtitle" style={{ marginBottom: '12px' }}>
                        <Paperclip size={16} />
                        Files to be uploaded ({fileList.length})
                        </div>
                        <AntList
                        size="small"
                        dataSource={fileList}
                        renderItem={(file) => (
                            <AntList.Item
                            actions={[
                                <Tooltip title="Remove">
                                <Button
                                    type="text"
                                    icon={<Trash2 size={14} />}
                                    size="small"
                                    onClick={() => {
                                    const newFileList = fileList.filter(item => item.uid !== file.uid);
                                    setFileList(newFileList);
                                    }}
                                />
                                </Tooltip>
                            ]}
                            >
                            <AntList.Item.Meta
                                avatar={<FileText size={16} />}
                                title={<span style={{ fontSize: '13px' }}>{file.name}</span>}
                                description={`${(file.size / 1024).toFixed(2)} KB`}
                            />
                            </AntList.Item>
                        )}
                        />
                    </div>
                    )}
                </div>
                </Card>
              </Col>

              {/* Right Column - Settings */}
              <Col xs={24} lg={8}>
                {/* Ticket Linking Card - AT THE TOP */}
                <Card className="kb-form-section">
                  <div className="kb-section-title">
                    <LinkIcon size={18} />
                    Link to Ticket
                  </div>
                  
                  <div className="kb-switch-container">
                    <div className="kb-switch-label">
                      <LinkIcon size={16} />
                      <span>Link to Existing Ticket</span>
                    </div>
                    <Switch
                      checked={showTicketLink || !!linkedTicket}
                      onChange={(checked) => {
                        setShowTicketLink(checked);
                        if (!checked && linkedTicket) {
                          clearLinkedTicket();
                        }
                      }}
                      disabled={!!linkedTicket}
                    />
                  </div>

                  {(showTicketLink || linkedTicket) && (
                    <Form.Item
                      label="Select Ticket"
                      name="ticketId"
                      rules={linkedTicket ? [] : [{ required: showTicketLink, message: 'Please select a ticket' }]}
                    >
                      <Select
                        size="large"
                        placeholder="Search tickets by ID or title..."
                        showSearch
                        optionFilterProp="children"
                        filterOption={(input, option) => {
                          const ticket = ticketOptions.find(t => t.id === option.value);
                          if (!ticket) return false;
                          const searchText = input.toLowerCase();
                          return (
                            ticket.uuid.toLowerCase().includes(searchText) ||
                            ticket.title.toLowerCase().includes(searchText)
                          );
                        }}
                        onChange={handleTicketSelect}
                        className="kb-form-select"
                        loading={fetchingData || fetchingTicketDetails}
                        disabled={!!linkedTicket}
                        allowClear={!linkedTicket}
                      >
                        {ticketOptions.map(ticket => (
                          <Option key={ticket.id} value={ticket.id}>
                            <div className="kb-ticket-option">
                              <div className="kb-ticket-option-main">
                                <strong>{ticket.uuid}</strong> - {ticket.title}
                              </div>
                              <div className="kb-ticket-option-sub">
                                <Clock size={12} />
                                Created: {ticket.formattedDate}
                              </div>
                            </div>
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  )}
                </Card>

                {/* Article Settings Card - ADDED MISSING FIELDS */}
                <Card className="kb-form-section">
                  <div className="kb-section-title">
                    <Settings size={18} />
                    Article Settings
                  </div>
                  
                  <Form.Item
                    label="Article Type"
                    name="type_name"
                    rules={[{ required: true, message: 'Please select article type' }]}
                  >
                    <Select 
                      size="large" 
                      placeholder="Select article type" 
                      className="kb-form-select"
                      showSearch
                      optionFilterProp="children"
                      filterOption={(input, option) =>
                        option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                      }
                    >
                      {ARTICLE_TYPES.map(type => (
                        <Option key={type.id} value={type.name}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {type.icon}
                            <span>{type.name}</span>
                          </div>
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>

                  {/* ADDED: Ticket Category Field */}
                  <Form.Item
                    label="Ticket Category"
                    name="category"
                  >
                    <Select 
                      size="large" 
                      placeholder="Select ticket category" 
                      className="kb-form-select"
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      loading={fetchingData}
                    >
                      <Option value="">None</Option>
                      {categoryOptions.map(category => {
                        const categoryName = category.name || category.category_name || category;
                        const categoryValue = category.id || categoryName;
                        return (
                          <Option key={categoryValue} value={categoryName}>
                            {categoryName}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>

                  {/* ADDED: Product Field */}
                  <Form.Item
                    label="Product"
                    name="product"
                  >
                    <Select 
                      size="large" 
                      placeholder="Select product" 
                      className="kb-form-select"
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      loading={fetchingData}
                    >
                      <Option value="">None</Option>
                      {productOptions.map(product => {
                        const productName = product.name || product.product_name || product;
                        return (
                          <Option key={productName} value={productName}>
                            {productName}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>

                  {/* ADDED: Product Category Field */}
                  <Form.Item
                    label="Product Category"
                    name="productCategory"
                  >
                    <Select 
                      size="large" 
                      placeholder="Select product category" 
                      className="kb-form-select"
                      allowClear
                      showSearch
                      filterOption={(input, option) =>
                        (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                      }
                      loading={fetchingData}
                    >
                      <Option value="">None</Option>
                      {productCategoryOptions.map(category => {
                        const categoryName = category.name || category.category || category.category_name || category;
                        return (
                          <Option key={categoryName} value={categoryName}>
                            {categoryName}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>

                  {/* ADDED: Priority Field */}
                  <Form.Item
                    label="Priority"
                    name="priority"
                  >
                    <Select 
                      size="large" 
                      placeholder="Select priority" 
                      className="kb-form-select"
                      allowClear
                      loading={fetchingData}
                    >
                      <Option value="">None</Option>
                      {priorityOptions.map(priority => {
                        const priorityName = priority.name || priority.priority_name || priority;
                        const priorityId = priority.id || priority.priority_id;
                        return (
                          <Option key={priorityId} value={priorityId}>
                            {priorityName}
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Status"
                    name="status"
                    initialValue="draft"
                  >
                    <Select size="large" className="kb-form-select">
                      <Option value="draft">Draft</Option>
                      <Option value="published">Published</Option>
                      <Option value="archived">Archived</Option>
                    </Select>
                  </Form.Item>

                  <Form.Item
                    label="Tags"
                    name="tags"
                  >
                    <Select
                      mode="tags"
                      size="large"
                      placeholder="Add tags (press Enter)"
                      tokenSeparators={[',']}
                      className="kb-form-select"
                    />
                  </Form.Item>
                </Card>

                {/* Action Buttons */}
                <div className="kb-form-actions">
                  <Button
                    onClick={() => navigate('/knowledge-base')}
                    className="kb-cancel-button"
                    size="large"
                    disabled={loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="default"
                    className="kb-draft-button"
                    onClick={() => {
                      form.setFieldsValue({ status: 'draft' });
                      form.submit();
                    }}
                    loading={loading}
                    size="large"
                  >
                    Save as Draft
                  </Button>
                  <Button
                    type="primary"
                    icon={<Save size={16} />}
                    loading={loading}
                    onClick={() => {
                      form.setFieldsValue({ status: 'published' });
                      form.submit();
                    }}
                    className="kb-submit-button"
                    size="large"
                  >
                    Publish Article
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default CreateArticlePage;